import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  ArrowSquareOut,
  CreditCard,
  CheckCircle,
  Warning,
  Sparkle,
  XCircle,
  Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * /admin/stripe-issuing-demo
 *
 * Mint REAL Stripe Issuing TEST-MODE Visa cards on demand. Calls
 * `POST /api/admin/stripe-issuing/demo/mint` which returns a card with
 * a real Stripe-issued PAN/CVC/expiry — passes Luhn validation,
 * displays as a Visa to any code that parses card numbers, and shows
 * up in your Stripe Test-Mode Dashboard under Issuing → Cards.
 *
 * Test-mode cards CANNOT charge real merchants. They CAN be used to:
 *   • Screenshot for investor / partner pitches ("here's what a Zcardon
 *     Visa looks like end-to-end")
 *   • Send to family / dev testers as a tangible demo
 *   • Verify the StripeIssuingProvider adapter, webhook handler, and
 *     downstream reconciliation worker all work end-to-end before going live
 *   • Stripe's test_helpers API can simulate authorizations against them
 */

function MaskedReveal({ value, dataTestId }) {
  const [revealed, setRevealed] = useState(false);
  if (!value) return <span className="opacity-50 mono">—</span>;
  const masked = value.replace(/\d(?=\d{4})/g, "•");
  return (
    <button
      onClick={() => {
        setRevealed((r) => !r);
        if (!revealed) {
          navigator.clipboard.writeText(value);
          toast.success("Copied to clipboard");
        }
      }}
      className="mono text-sm tracking-wider hover:text-[#184A30] transition"
      title={revealed ? "Click to hide" : "Click to reveal + copy"}
      data-testid={dataTestId}
    >
      {revealed ? value : masked}
    </button>
  );
}

function DemoCard({ card, onCancel }) {
  // Pretty render — mirrors the live VirtualCard v2 layout (big QR on
  // the right) but uses the demo card's actual Stripe-issued PAN.
  const pan = card.pan || "";
  const grouped = pan.match(/.{1,4}/g)?.join(" ") || pan;
  const network = (card.network || "visa").toUpperCase();

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid={`demo-card-row-${card.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
            {network} · Stripe test-mode · {new Date(card.created_at).toLocaleString()}
          </p>
          <p className="serif text-lg mt-1 truncate" data-testid={`demo-card-cardholder-${card.id}`}>
            {card.cardholder_name}
          </p>
          <p className="text-xs text-[#4A5C50] mono">{card.cardholder_email}</p>
        </div>
        <div className="text-right">
          {card.status === "cancelled" ? (
            <span className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#4A5C50]/10 text-[#4A5C50] border border-[#4A5C50]/20">
              Cancelled
            </span>
          ) : card.status === "active" ? (
            <span className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
              Active
            </span>
          ) : (
            <span className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">
              {card.status}
            </span>
          )}
        </div>
      </div>

      {/* Visual card render (compact preview) */}
      {card.status === "active" && pan && (
        <div className="mt-4 relative rounded-2xl overflow-hidden text-[#FBF9F6] aspect-[1.586/1] max-w-md mx-auto"
             style={{ background: "linear-gradient(135deg, #112318, #0A1F16)" }}>
          <div className="absolute inset-0" style={{ background: "radial-gradient(120% 100% at 0% 0%, rgba(217,108,74,0.18) 0%, transparent 55%), radial-gradient(80% 80% at 100% 100%, rgba(139,168,136,0.18) 0%, transparent 60%)" }} />
          <div className="relative h-full flex p-5 gap-3">
            <div className="flex-1 min-w-0 flex flex-col justify-between">
              <div>
                <p className="serif text-base font-semibold tracking-wider">Cardon</p>
                <p className="text-[9px] opacity-60 tracking-widest mt-1">ISSUED BY</p>
                <p className="text-xs tracking-wider">Stripe Issuing · TEST MODE</p>
              </div>
              <div>
                <p className="mono text-[11px] md:text-sm tracking-[0.16em] font-semibold" data-testid={`demo-card-pan-${card.id}`}>
                  {grouped}
                </p>
                <div className="flex items-end justify-between mt-2 gap-2">
                  <div className="min-w-0">
                    <p className="text-[9px] opacity-60 tracking-widest">CARDHOLDER</p>
                    <p className="text-xs uppercase tracking-wider truncate">{card.cardholder_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[9px] opacity-60 tracking-widest">EXP · CVV</p>
                    <p className="mono text-xs font-semibold">{card.expiry || "••/••"}·{card.cvv || "•••"}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="self-center bg-white rounded-lg p-2 shrink-0">
              <QRCodeSVG value={`stripe-test-card-${card.last4}`} size={120} fgColor="#0A1F16" bgColor="#FFFFFF" />
            </div>
          </div>
        </div>
      )}

      {/* Sensitive fields with click-to-reveal */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">PAN</p>
          <MaskedReveal value={pan} dataTestId={`demo-card-pan-reveal-${card.id}`} />
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">CVC</p>
          <MaskedReveal value={card.cvv} dataTestId={`demo-card-cvv-reveal-${card.id}`} />
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Expiry</p>
          <p className="mono text-sm">{card.expiry || "—"}</p>
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Amount loaded</p>
          <p className="mono text-sm">${(card.amount_usd || 0).toFixed(2)}</p>
        </div>
      </div>

      <div className="mt-4 text-[10px] text-[#4A5C50] mono">
        Stripe card id: <span className="opacity-70">{card.provider_card_id || "—"}</span>
      </div>

      {card.status === "active" && (
        <div className="mt-3 flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCancel(card.id)}
            className="text-xs rounded-xl border-[#4A5C50] text-[#4A5C50] hover:bg-rose-50 hover:border-rose-300 hover:text-rose-800"
            data-testid={`demo-card-cancel-${card.id}`}
          >
            <XCircle size={14} weight="duotone" /> Cancel test card
          </Button>
        </div>
      )}

      {card.status === "failed" && card.error && (
        <div className="mt-3 p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-900">
          <p className="font-semibold">Mint failed</p>
          <p className="mono mt-1 break-all opacity-90">{card.error}</p>
        </div>
      )}
    </div>
  );
}

export default function StripeIssuingDemo() {
  const [providerStatus, setProviderStatus] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    cardholder_name: "Demo Receiver",
    cardholder_email: "demo@zcardon.test",
    amount_usd: 50,
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/issuance-provider/status"),
        api.get("/admin/stripe-issuing/demo/cards?limit=20"),
      ]);
      setProviderStatus(s.data);
      setCards(c.data?.cards || []);
    } catch (e) {
      toast.error("Could not load demo status: " + (e?.message || ""));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const mint = async () => {
    if (!form.cardholder_name.trim()) { toast.error("Cardholder name required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/stripe-issuing/demo/mint", form);
      toast.success("Test Visa card minted: •••• " + r.data.last4);
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Mint failed";
      toast.error(detail, { duration: 12000 });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (cardId) => {
    if (!confirm("Cancel this test card on Stripe? It will be permanently revoked.")) return;
    try {
      await api.post(`/admin/stripe-issuing/demo/${cardId}/cancel`);
      toast.success("Card cancelled");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Cancel failed");
    }
  };

  const stripeEnv = providerStatus?.env_status?.stripe || {};
  const isLive = stripeEnv.live_key;
  const isTest = stripeEnv.test_key;
  const keyPrefix = stripeEnv.key_prefix || "(none)";

  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#112318]" data-testid="stripe-issuing-demo">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/admin/stripe-issuing-guide" className="inline-flex items-center gap-1 text-sm text-[#4A5C50] hover:text-[#184A30]" data-testid="demo-back">
          <ArrowLeft size={14} /> Back to Stripe Issuing setup guide
        </Link>

        <header className="mt-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Stripe Issuing · Test mode</p>
          <h1 className="serif text-4xl mt-1 flex items-center gap-3">
            <Sparkle size={32} weight="duotone" className="text-[#D96C4A]" /> Mint a real Visa test card
          </h1>
          <p className="text-[#4A5C50] mt-2 leading-relaxed max-w-2xl text-sm">
            These are <b>real Stripe Issuing test-mode cards</b> with Luhn-valid
            PANs, real CVCs, and real expiry dates. They display in your Stripe
            Dashboard under Issuing → Cards. They <b>cannot charge real
            merchants</b> — perfect for investor demos, partner conversations,
            and validating the end-to-end pipeline before going live.
          </p>
        </header>

        {/* Key mode banner */}
        <section
          className={`mt-6 rounded-2xl border p-5 ${
            isTest
              ? "bg-emerald-50 border-emerald-200"
              : isLive
              ? "bg-[#CBA35C]/10 border-[#CBA35C]/40"
              : "bg-rose-50 border-rose-200"
          }`}
          data-testid="demo-key-status"
        >
          {loading ? (
            <p className="text-sm text-[#4A5C50] mono">Checking Stripe key mode…</p>
          ) : isTest ? (
            <>
              <p className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                <CheckCircle size={16} weight="fill" /> Test-mode Stripe key detected ({keyPrefix})
              </p>
              <p className="text-xs text-emerald-800 mt-1">
                Demo minting is enabled. Click "Mint test Visa card" below.
              </p>
            </>
          ) : isLive ? (
            <>
              <p className="text-sm font-semibold text-[#8C6B21] flex items-center gap-2">
                <Warning size={16} weight="fill" /> LIVE Stripe key detected — demo blocked for safety
              </p>
              <p className="text-xs text-[#8C6B21] mt-2 leading-relaxed">
                To unlock test-mode demos, add a separate test-mode key to your
                env vars. This keeps your live Issuing balance fully isolated:
              </p>
              <ol className="list-decimal list-inside text-xs text-[#8C6B21] mt-2 space-y-1">
                <li>
                  Open{" "}
                  <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">
                    Stripe Dashboard → Test mode → API keys <ArrowSquareOut size={11} />
                  </a>
                </li>
                <li>Copy the <span className="mono">sk_test_...</span> secret key</li>
                <li>Add it on Emergent Deploy Panel as <span className="mono">STRIPE_TEST_API_KEY</span></li>
                <li>Redeploy + come back here</li>
              </ol>
              <p className="text-xs text-[#8C6B21] mt-3 leading-relaxed">
                Also <b>enable Stripe Issuing in test mode</b> at{" "}
                <a href="https://dashboard.stripe.com/test/issuing/overview" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">
                  test/issuing/overview <ArrowSquareOut size={11} />
                </a>{" "}
                (one-click, no approval needed). Without this, you'll get a
                "no PAN" error even with a valid test key.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-rose-900 flex items-center gap-2">
                <XCircle size={16} weight="fill" /> No Stripe key configured
              </p>
              <p className="text-xs text-rose-800 mt-1">
                Set <span className="mono">STRIPE_TEST_API_KEY</span> (or{" "}
                <span className="mono">STRIPE_API_KEY</span> with an sk_test_*
                value) on your Deploy Panel.
              </p>
            </>
          )}
        </section>

        {/* Mint form */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-6" data-testid="demo-mint-form">
          <h2 className="serif text-2xl">Mint a new test Visa card</h2>
          <p className="text-xs text-[#4A5C50] mt-1">
            Issued instantly. Saved here for repeat reference. Cancel any time.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-[#4A5C50]">Cardholder name</span>
              <input
                type="text"
                value={form.cardholder_name}
                onChange={(e) => setForm({ ...form, cardholder_name: e.target.value })}
                className="mt-1 w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm mono"
                data-testid="demo-field-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-[#4A5C50]">Cardholder email</span>
              <input
                type="email"
                value={form.cardholder_email}
                onChange={(e) => setForm({ ...form, cardholder_email: e.target.value })}
                className="mt-1 w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm mono"
                data-testid="demo-field-email"
              />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest uppercase text-[#4A5C50]">Load amount (USD)</span>
              <input
                type="number"
                min="1"
                max="10000"
                step="1"
                value={form.amount_usd}
                onChange={(e) => setForm({ ...form, amount_usd: Number(e.target.value) || 0 })}
                className="mt-1 w-full border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm mono"
                data-testid="demo-field-amount"
              />
            </label>
          </div>
          <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-[#4A5C50] flex items-center gap-1.5">
              <Info size={13} weight="duotone" /> Cards appear in your{" "}
              <a href="https://dashboard.stripe.com/test/issuing/cards" target="_blank" rel="noopener noreferrer" className="underline text-[#184A30]">
                Stripe Dashboard
              </a>{" "}
              instantly.
            </p>
            <Button
              onClick={mint}
              disabled={busy || (!isTest && !providerStatus?.env_status?.test_key)}
              className="rounded-xl bg-[#D96C4A] hover:bg-[#C75A3A] text-white"
              data-testid="demo-mint-btn"
            >
              <CreditCard size={16} weight="duotone" /> {busy ? "Minting on Stripe…" : "Mint test Visa card"}
            </Button>
          </div>
        </section>

        {/* List of demo cards */}
        <section className="mt-8" data-testid="demo-cards-list">
          <h2 className="serif text-2xl">Recent test cards</h2>
          {loading ? (
            <p className="text-sm text-[#4A5C50] mt-3 mono">Loading…</p>
          ) : cards.length === 0 ? (
            <div className="mt-3 rounded-2xl border border-[#E5E0D8] bg-white p-6 text-center" data-testid="demo-cards-empty">
              <p className="text-sm text-[#4A5C50]">
                No demo cards yet. Use the form above to mint your first one.
              </p>
            </div>
          ) : (
            <div className="mt-3 space-y-4">
              {cards.map((c) => (
                <DemoCard key={c.id} card={c} onCancel={cancel} />
              ))}
            </div>
          )}
        </section>

        <footer className="mt-10 text-xs text-[#4A5C50] leading-relaxed">
          Test cards live on your Stripe account under{" "}
          <a href="https://dashboard.stripe.com/test/issuing/cards" target="_blank" rel="noopener noreferrer" className="underline text-[#184A30]">
            test/issuing/cards
          </a>. When you go LIVE, customers will mint <i>real</i> Visa cards
          via the normal <span className="mono">/api/cards/issue</span> flow — this demo
          page intentionally bypasses that flow + uses your separate test key.
        </footer>
      </div>
    </div>
  );
}
